import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter } from 'lucide-react';
import { settingsApi } from '@/services/api';

const Footer = () => {
  const { t } = useTranslation();
  const { isRTL, currentLang } = useLanguage();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    settingsApi.get().then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const isAr = currentLang === 'ar';
  // Pick the localized value, falling back to the other language, then to a default.
  const pick = (en, ar, fallback = '') =>
    (isAr ? (ar || en) : (en || ar)) || fallback;

  const s = settings || {};
  const social = s.social_links || {};

  const quickLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.projects'), path: '/projects' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const phone = s.phone || '+966593591595';
  const email = s.email || 'info@armankitchens.com';
  const address = pick(s.address_en, s.address_ar, isAr ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Saudi Arabia');
  const description = pick(s.footer_description_en, s.footer_description_ar) || t('footer.description');

  const hoursHeading = isAr ? 'ساعات العمل' : 'Working Hours';
  const hoursLine1Label = pick(s.footer_hours_line1_label_en, s.footer_hours_line1_label_ar, isAr ? 'السبت - الخميس' : 'Sat - Thu');
  const hoursLine1Value = pick(s.footer_hours_line1_value_en, s.footer_hours_line1_value_ar, '9:00 - 21:00');
  const hoursLine2Label = pick(s.footer_hours_line2_label_en, s.footer_hours_line2_label_ar, isAr ? 'الجمعة' : 'Friday');
  const hoursLine2Value = pick(s.footer_hours_line2_value_en, s.footer_hours_line2_value_ar, isAr ? 'مغلق' : 'Closed');

  const copyrightName = s.footer_copyright_name || 'Arman Kitchens';
  const privacyUrl = s.footer_privacy_url || '/privacy-policy';
  const termsUrl = s.footer_terms_url || '#';

  // Only render a social icon when a URL is configured for it.
  const socialItems = [
    { key: 'instagram', url: social.instagram, Icon: Instagram, testid: 'social-instagram' },
    { key: 'facebook', url: social.facebook, Icon: Facebook, testid: 'social-facebook' },
    { key: 'twitter', url: social.twitter, Icon: Twitter, testid: 'social-twitter' },
  ].filter((item) => item.url && item.url.trim());

  return (
    <footer data-testid="main-footer" className="bg-brand-dark text-white grain">
      <div className="container-custom section-padding relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <span className="text-3xl font-bold tracking-tight">ARMAN</span>
              <span className="text-gold text-sm uppercase tracking-[0.3em] ms-2">Kitchens</span>
            </Link>
            <p className="text-white/60 leading-relaxed mb-6">
              {description}
            </p>
            {socialItems.length > 0 && (
              <div className="flex gap-4">
                {socialItems.map(({ key, url, Icon, testid }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={testid}
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-charcoal transition-colors duration-300"
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                    className="text-white/60 hover:text-gold transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('footer.contactInfo')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-gold mt-1 flex-shrink-0" />
                <a
                  href={`tel:${phone.replace(/\s+/g, '')}`}
                  data-testid="footer-phone"
                  className="text-white/60 hover:text-gold transition-colors"
                  dir="ltr"
                >
                  {phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-gold mt-1 flex-shrink-0" />
                <a
                  href={`mailto:${email}`}
                  data-testid="footer-email"
                  className="text-white/60 hover:text-gold transition-colors"
                >
                  {email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-gold mt-1 flex-shrink-0" />
                <span className="text-white/60">
                  {address}
                </span>
              </li>
            </ul>
          </div>

          {/* Working Hours */}
          <div>
            <h4 className="text-lg font-semibold mb-6">
              {hoursHeading}
            </h4>
            <ul className="space-y-3 text-white/60">
              <li className="flex justify-between">
                <span>{hoursLine1Label}</span>
                <span>{hoursLine1Value}</span>
              </li>
              <li className="flex justify-between">
                <span>{hoursLine2Label}</span>
                <span>{hoursLine2Value}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {copyrightName}. {t('footer.rights')}
          </p>
          <div className="flex gap-6 text-sm text-white/40">
            <a href={privacyUrl} className="hover:text-gold transition-colors">
              {isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </a>
            <a href={termsUrl} className="hover:text-gold transition-colors">
              {isAr ? 'الشروط والأحكام' : 'Terms & Conditions'}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
